import {
  trackEvent,
  type AnalyticsEvent,
  type AnalyticsEventData,
} from './analytics';
import { flagClaritySession, isLocalRun } from './clarity';

/** Problems players hit, as opposed to what they do (analytics.ts). See
 *  docs/clarity-launch-day.md for how to find them in the dashboards. */
export type ProblemEvent = Extract<
  AnalyticsEvent,
  | 'app_error'
  | 'chunk_load_failed'
  | 'sw_register_failed'
  | 'live_share_failed'
  | 'storage_failed'
  | 'link_copy_failed'
>;

/** A tag only has to say which problem it was. */
const MAX_MESSAGE = 100;
/** An error in a loop (every frame, every tap) must not flood either vendor
 *  or use up Vercel's event quota. */
const MAX_REPORTS = 10;
const reported = new Set<string>();

// JSON.parse quotes the start of the text it failed on (V8, WebKit), and from
// a saved game that's names. Greedy, so nested quotes go too.
const QUOTED = /["“”`][\s\S]*["“”`]/g;
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const URL_SUFFIX = /(https?:\/\/[^\s?#]+)[?#]\S*/g;

/** The error's name and message as a short tag, without names, session IDs
 *  or link tokens. */
function errorMessage(error: unknown): string | undefined {
  const raw =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : undefined;
  if (!raw) return undefined;
  // The site's own origin goes, so a failed file's name fits in the tag. An
  // opaque origin is the string "null", which messages also contain.
  const { origin } = window.location;
  const clean = raw
    .replace(QUOTED, '"…"')
    .replace(UUID, '…')
    .replace(URL_SUFFIX, '$1')
    .replaceAll(origin.startsWith('http') ? origin : '\0', '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean.length > MAX_MESSAGE
    ? `${clean.slice(0, MAX_MESSAGE - 1)}…`
    : clean;
}

/**
 * Reports a problem to Vercel and Clarity as a schema event, and in Clarity
 * only, tags the error's message and keeps the session's full recording. Each
 * problem is reported once per page load. Dev and E2E runs send nothing.
 */
export function reportProblem(
  name: ProblemEvent,
  data?: AnalyticsEventData,
  error?: unknown,
): void {
  try {
    if (typeof window === 'undefined' || isLocalRun()) return;
    const message = errorMessage(error);
    const key = JSON.stringify([name, data, message]);
    if (reported.has(key) || reported.size >= MAX_REPORTS) return;
    reported.add(key);
    flagClaritySession(name, message);
    trackEvent(name, data);
  } catch {
    // Reporting a problem must never cause one.
  }
}

// Browsers report these as errors, but nothing broke: a cross-origin script's
// error carries no details, and the ResizeObserver one is a warning.
const HARMLESS = [/^Script error\.?$/, /ResizeObserver loop/];

function onError(event: ErrorEvent) {
  if (!event.error && HARMLESS.some((pattern) => pattern.test(event.message)))
    return;
  reportProblem(
    'app_error',
    { error_source: 'error' },
    event.error ?? event.message,
  );
}

function onRejection(event: PromiseRejectionEvent) {
  reportProblem('app_error', { error_source: 'rejection' }, event.reason);
}

/** Reports uncaught errors and unhandled promise rejections; returns a
 *  function that stops. */
export function installErrorReporting(): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

/** React's `onCaughtError`: an error boundary (React Router has one around
 *  each screen) caught a render error and shows its error screen instead. */
export function onCaughtRenderError(
  error: unknown,
  info: { componentStack?: string },
): void {
  reportProblem('app_error', { error_source: 'render' }, error);
  // This replaces React's default handler, which logs the error.
  console.error(error, info.componentStack);
}

interface RouterErrors {
  errors: Record<string, unknown> | null;
}

/** A screen whose code fails to load shows React Router's error screen
 *  without throwing, so nothing else hears of it. Returns a function that
 *  stops. */
export function reportRouteErrors(router: {
  subscribe(listener: (state: RouterErrors) => void): () => void;
}): () => void {
  return router.subscribe(({ errors }) => {
    for (const error of Object.values(errors ?? {})) {
      reportProblem('app_error', { error_source: 'route' }, error);
    }
  });
}

export function resetErrorReportingForTests(): void {
  reported.clear();
}
